import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Airplane, Trash, FloppyDisk } from "@phosphor-icons/react";

const LEAVE_TYPE_OPTS = [
  { v: "paid", label: "Congé payé" },
  { v: "rtt", label: "RTT" },
  { v: "sick", label: "Maladie" },
  { v: "unpaid", label: "Sans solde" },
];

/**
 * Edit / delete dialog for an approved leave.
 * Opens when an admin clicks a green "CONGÉ" cell on the agenda.
 */
export default function LeaveEditDialog({ open, onOpenChange, leave, onChanged }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !leave) return;
    setForm({
      type: leave.type || "paid",
      start_date: leave.start_date || "",
      end_date: leave.end_date || "",
      reason: leave.reason || "",
    });
  }, [open, leave?.id]);

  if (!leave) return null;

  const days = (() => {
    if (!form.start_date || !form.end_date || form.end_date < form.start_date) return 0;
    const s = new Date(form.start_date), e = new Date(form.end_date);
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  })();

  const dirty =
    form.type !== leave.type ||
    form.start_date !== leave.start_date ||
    form.end_date !== leave.end_date ||
    (form.reason || "") !== (leave.reason || "");

  const save = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Dates requises"); return; }
    if (form.end_date < form.start_date) { toast.error("Fin doit être postérieure au début"); return; }
    setSaving(true);
    try {
      await api.put(`/leave-requests/${leave.id}`, {
        type: form.type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: (form.reason || "").trim() || null,
      });
      onOpenChange(false);
      setTimeout(() => toast.success("Congé mis à jour"), 50);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer ce congé de ${leave.user_name} ? Le solde sera recrédité.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/leave-requests/${leave.id}`);
      onOpenChange(false);
      setTimeout(() => toast.success(`Congé supprimé pour ${leave.user_name}`), 50);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="leave-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Airplane size={20} className="text-emerald-400" weight="duotone" />
            Congé — {leave.user_name}
          </DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50 mt-1">
            Créé le {(leave.created_at || "").slice(0, 10)}
            {leave.decided_by_name && <> · validé par {leave.decided_by_name}</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="leave-edit-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                {LEAVE_TYPE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Du *">
              <Input
                type="date"
                value={form.start_date || ""}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="bg-white/5 border-white/10 [color-scheme:dark]"
                data-testid="leave-edit-start"
              />
            </Field>
            <Field label="Au *">
              <Input
                type="date"
                value={form.end_date || ""}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                className="bg-white/5 border-white/10 [color-scheme:dark]"
                data-testid="leave-edit-end"
              />
            </Field>
          </div>

          {days > 0 && (
            <div className="text-[10px] text-white/50 text-center">
              Soit <span className="font-mono text-emerald-300">{days} jour{days > 1 ? "s" : ""}</span>
              {days !== leave.days && <> (avant : {leave.days}j)</>}
            </div>
          )}

          <Field label="Motif (optionnel)">
            <Textarea
              value={form.reason || ""}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              className="bg-white/5 border-white/10 min-h-[70px]"
              placeholder="Ex: mariage, rendez-vous médical, repos…"
              data-testid="leave-edit-reason"
            />
          </Field>
        </div>

        <div className="flex gap-2 mt-4">
          <Button
            onClick={remove}
            disabled={deleting || saving}
            variant="ghost"
            className="border border-red-500/40 text-red-300 hover:bg-red-500/10"
            data-testid="leave-edit-delete"
          >
            <Trash size={13} weight="bold" className="mr-1" /> {deleting ? "…" : "Supprimer"}
          </Button>
          <Button
            variant="ghost"
            className="flex-1 border border-white/10"
            onClick={() => onOpenChange(false)}
          >
            Fermer
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving || deleting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
            data-testid="leave-edit-save"
          >
            <FloppyDisk size={13} weight="bold" className="mr-1" /> {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-white/60 text-[11px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}
